import {
  useState, useEffect, useRef 
} from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

//Themes
import theme from '@/themes/index';

export const useModal = () => {
  const [isShowing, setIsShowing] = useState(false);

  function toggle() {
    setIsShowing(!isShowing);
  }

  return {
    isShowing,
    toggle,
  };
};

// Hook
export const useOnClickOutside = (ref, handler) => {
  useEffect(
    () => {
      const listener = event => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);

      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
};

const propTypes = {
  children: PropTypes.any.isRequired,
  isShowing: PropTypes.bool.isRequired,
  isCloseOnClickOutside: PropTypes.bool,
  footer: PropTypes.func,
  header: PropTypes.string,
  hide: PropTypes.func,
};

const defaultProps = {
  isCloseOnClickOutside: false,
  footer: null,
  header: null,
  hide: false,
};

const Modal = ({
  children,
  isShowing,
  isCloseOnClickOutside,
  footer,
  header,
  hide,
}) => {
  let modalRef;

  if (isCloseOnClickOutside) {
    modalRef = useRef();
    useOnClickOutside(modalRef, () => hide());
  }

  return isShowing
    ? createPortal(
      <>
        <div className='modal-overlay'>
          <div
            className='modal-wrapper'
            aria-modal
            aria-hidden
            tabIndex={-1}
            role='dialog'
          >
            <div ref={isCloseOnClickOutside && modalRef} className='modal'>
              {header && (
                <div className='modal-header'>
                  <h6>This is a sample header</h6>
                </div>
              )}
              <button className='close-modal' onClick={hide}>
                <FontAwesomeIcon icon={['fa', 'times']} />
              </button>
              <div className='modal-body'>{children}</div>
              {footer && <div className='modal-footer'>{footer()}</div>}
            </div>
          </div>
        </div>
        <style jsx>{`
            div.modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              z-index: 1040;
              width: 100vw;
              height: 100vh;
              background-color: rgba(84, 86, 90, 0.7);
            }

            div.modal-overlay div.modal-wrapper {
              position: fixed;
              top: 0;
              left: 0;
              z-index: 1050;
              width: 100%;
              height: 100%;
              overflow-x: hidden;
              overflow-y: auto;
              outline: 0;
            }

            div.modal-overlay div.modal-wrapper div.modal {
              z-index: 100;
              position: relative;
              margin: 1.75rem auto;
              border-radius: 3px;
              max-width: 900px;
              min-width: 400px;
              padding: 2rem;
              background-color: ${theme.backgroundColors.primary};
            }

            button.close-modal {
              display: flex;
              justify-content: center;
              align-items: center;
              position: absolute;
              top: 0.5em;
              right: 0.3em;
              padding: 9px;
              font-size: 1.5em;
              border: none;
              cursor: pointer;
              background-color: white;
              z-index: 1302;
            }

            div.modal-header {
              display: flex;
              justify-content: flex-start;
            }

            div.modal-header h6 {
              line-height: 1em;
              margin-block-start: 0;
              margin-block-end: 0;
            }

            div.modal-body {
              padding: 3em 0;
            }

            button.close-modal:hover {
              color: ${theme.backgroundColors.optional1};
            }
          `}</style>
      </>,
      document.body
    )
    : null;
};

Modal.propTypes = propTypes;
Modal.defaultProps = defaultProps;

export default Modal;
